import { FieldArray, Formik } from "formik";
import { useEffect, useState } from "react";
import Spin from "../../../../components/Spin";
import { Label } from "../../../../components/ui/label";
import CustomLabelText from "../../../../components/CustomLabelText";
import { Input } from "../../../../components/ui/input";
import { API, fileUpload, get } from "../../../../config";
import SearchSelect from "../../../../components/SearchSelect";
import { Button } from "../../../../components/ui/button";
import { AddAdminConditionSchema } from "../../../../lib/Validation";
import { sectionTypeOptions } from "../../../../config";
import { Loader2, Plus, Trash2 } from "lucide-react";
import { Card } from "../../../../components/ui/card";
import { Checkbox } from "../../../../components/ui/checkbox";
import { MultiSelect } from "../../../../components/SearchMultiSelect";
import ColorPicker from "../../../../components/ColorPicker";
import { useAllOrganizationTypes } from "../../../Auth/SignUpHelper";

const AddEditCondition = ({
  id,
  toggleShowAddEditCondition,
  edit,
  fetchConditionData,
}) => {
  const [conditionData, setConditionData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [sectionLoading, setSectionLoading] = useState(false);
  const [sectionListing, setSectionListing] = useState([]);
  const [sectionList, setSectionList] = useState([]);

  const [versionLoading, setVersionLoading] = useState(false);
  const [versionListing, setVersionListing] = useState([]);
  const [versionList, setVersionList] = useState([]);

  const { allOrganizationTypeLoading, allOrganizationType = [] } =
    useAllOrganizationTypes({ amlCTFType: true });
  const organizationType = allOrganizationType?.map((item) => {
    const { id: value, name, short_name, ...rest } = item;
    return { label: name, value, ...rest };
  });

  const operatorOptions = [
    { label: "Greater than", value: "Greater than" },
    { label: "Less than", value: "Less than" },
    { label: "Equal to", value: "Equal to" },
    { label: "Between", value: "Between" },
  ];

  useEffect(() => {
    if (sectionList) {
      let sectionTempListing = sectionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: item?.title,
      }));
      setSectionListing(sectionTempListing || []);
    } else {
      setSectionListing([]);
    }
  }, [sectionList]);

  async function fetchData() {
    try {
      setLoading(true);
      const reponse = await get(`${API.ADMIN_CONDITION_DETAILS}/` + id);
      AdminSectionList(
        reponse?.data?.data?.organization_type_id,
        reponse?.data?.data?.section_type
      );
      setConditionData(reponse?.data?.data);
      setLoading(false);
    } catch (e) {
      setConditionData(null);
    }
  }

  useEffect(() => {
    if (edit) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleFormSubmit = async (value) => {
    const formData = new FormData();

    formData.append(
      "organization_type_id",
      value?.organization_type_id ? value?.organization_type_id : ""
    );
    formData.append("version_id", value?.version_id ? value?.version_id : "");
    formData.append(
      "section_type",
      value?.section_type ? value?.section_type : ""
    );
    formData.append("order", value?.order ? value?.order : "");
    formData.append("risk_level", value?.risk_level ? value?.risk_level : "");
    formData.append(
      "risk_action",
      value?.risk_action ? value?.risk_action : ""
    );
    formData.append("color", value?.color ? value?.color : "");
    formData.append("default", value?.default ? 1 : 0);

    Object.keys(value).forEach((key) => {
      if (key === "conditions") {
        for (let c = 0; c < value[key]?.length; c++) {
          Object.keys(value[key][c]).forEach((conKey) => {
            if (conKey === "sections") {
              for (let s = 0; s < value[key][c][conKey]?.length; s++) {
                formData.append(
                  `conditions[${c}][sections][${s}]`,
                  value[key][c][conKey][s]
                );
              }
            } else {
              formData.append(
                `conditions[${c}][${conKey}]`,
                value[key][c][conKey]
              );
            }
          });
        }
      }
    });

    if (edit) {
      formData.append("_method", "put");
    }
    try {
      setLoading(true);
      const { data } = await fileUpload(
        `${API.ADMIN_CONDITION_ADD}${edit ? `/${id}` : ""}`,
        formData
      );
      setLoading(false);
      if (data) {
        toggleShowAddEditCondition();
        fetchConditionData();
      }
      return data?.data;
    } catch (error) {
      setLoading(false);
    }
  };

  const AdminSectionList = async (organizationType, sectionType) => {
    if (sectionType && organizationType) {
      try {
        setSectionLoading(true);

        const { data } = await get(
          `${API.GET_ADMIN_SECTIONS}?organization_type_id=${organizationType}&section_type=${sectionType}&limit=-1&page=1`
        );

        setSectionLoading(false);

        setSectionList(() => [...data.data.data]);
      } catch (e) {
        setSectionList([]);
        setSectionLoading(false);
        return false;
      }
    }
  };

  const AdminVersionList = async () => {
    try {
      setVersionLoading(true);

      const { data } = await get(`${API.GET_ADMIN_VERSIONS}?limit=-1&page=1`);

      setVersionLoading(false);

      setVersionList(() => [...data.data.data]);
    } catch (e) {
      setVersionList([]);
      setVersionLoading(false);
      return false;
    }
  };

  useEffect(() => {
    AdminVersionList();
  }, []);

  useEffect(() => {
    if (versionList) {
      let versionTempListing = versionList?.map((item) => ({
        ...item,
        value: item?.id,
        label: `${item?.version_number} - ${item?.name}`,
      }));
      setVersionListing(versionTempListing || []);
    } else {
      setVersionListing([]);
    }
  }, [versionList]);

  return (
    <Formik
      initialValues={{
        organization_type_id: conditionData?.organization_type_id,
        version_id: conditionData?.version_id,
        section_type: conditionData?.section_type,
        order: conditionData?.order,
        risk_level: conditionData?.risk_level,
        risk_action: conditionData?.risk_action,
        color: conditionData?.color ? conditionData?.color : "#000000",
        default: conditionData?.default ? true : false,
        conditions: conditionData?.conditions
          ? conditionData?.conditions
          : [
              {
                sections: [],
                operator: "",
                total: "",
                min: "",
                max: "",
              },
            ],
      }}
      enableReinitialize={true}
      onSubmit={handleFormSubmit}
      validateOnBlur={true}
      validateOnChange={true}
      validationSchema={AddAdminConditionSchema}
    >
      {({
        values,
        errors,
        handleChange,
        handleBlur,
        handleSubmit,
        setFieldValue,
        isValid,
      }) => (
        <>
          {loading && <Spin />}
          <form
            onSubmit={handleSubmit}
            noValidate
            className="entry-condition md:m-0 m-5"
          >
            <div className="form-box overflow-y-auto overflow-x-hidden max-h-[80vh] p-4 border rounded-lg">
              <div className="grid gap-4">
                <div className="grid md:grid-cols-5 gap-4">
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Industry Type"
                        errorField={errors?.organization_type_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={organizationType}
                        optionType={"Industry Type"}
                        handleSelect={(e) => {
                          setFieldValue("organization_type_id", e);
                          const updatedConditions = values.conditions.map(
                            (condition) => ({
                              ...condition,
                              sections: [],
                            })
                          );

                          setFieldValue("conditions", updatedConditions);
                          setSectionList([]);
                          AdminSectionList(e, values?.section_type);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.organization_type_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.organization_type_id}
                        disabled={false}
                      />
                      {allOrganizationTypeLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="grid gap-2">
                    <Label htmlFor="type">
                      <CustomLabelText
                        required={true}
                        labelText="Version Number"
                        errorField={errors?.version_id}
                        bottomAlign={true}
                      />
                    </Label>
                    <div className="relative">
                      <SearchSelect
                        frameworks={versionListing}
                        optionType={"Version Number"}
                        handleSelect={(e) => {
                          setFieldValue("version_id", e);
                        }}
                        handleBlur={handleBlur}
                        className={
                          errors?.version_id &&
                          `focus-visible:ring-red-500 border-red-500`
                        }
                        initialValue={values?.version_id}
                        disabled={false}
                      />
                      {versionLoading && (
                        <span className="absolute top-2 right-2 animate-spin">
                          <Loader2 width={20} height={20} />
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Section Type */}
                  <div className="grid gap-2">
                    <Label htmlFor="section_type">
                      <CustomLabelText
                        required={true}
                        labelText="Section Type"
                        errorField={errors?.section_type}
                        bottomAlign={true}
                      />
                    </Label>
                    <SearchSelect
                      frameworks={sectionTypeOptions}
                      optionType={"Section Type"}
                      handleSelect={(e) => {
                        setFieldValue("section_type", e);
                        const updatedConditions = values.conditions.map(
                          (condition) => ({
                            ...condition,
                            sections: [],
                          })
                        );

                        setFieldValue("conditions", updatedConditions);
                        setSectionList([]);
                        AdminSectionList(values?.organization_type_id, e);
                      }}
                      handleBlur={handleBlur}
                      disabled={false}
                      initialValue={values?.section_type}
                      className={
                        errors?.section_type &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  {/* Order */}
                  <div className="grid gap-2">
                    <Label htmlFor="order">
                      <CustomLabelText
                        required={true}
                        labelText="Order"
                        errorField={errors?.order}
                        bottomAlign={true}
                      />
                    </Label>
                    <Input
                      id="order"
                      type="number"
                      name="order"
                      min={1} // 🚫 no 0 or negatives
                      step={1}
                      inputMode="numeric"
                      pattern="[0-9]*"
                      value={values?.order || ""}
                      onKeyDown={(e) => {
                        // block dot, minus, exponent
                        if ([".", "-", "e", "E"].includes(e.key)) {
                          e.preventDefault();
                        }
                      }}
                      onChange={(e) => {
                        const value = e.target.value;

                        // allow empty while typing
                        if (value === "") {
                          handleChange(e);
                          return;
                        }

                        // only whole numbers greater than 0
                        if (/^[1-9]\d*$/.test(value)) {
                          handleChange(e);
                        }
                      }}
                      onBlur={handleBlur}
                      autoComplete="off"
                      className={
                        errors?.order &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  {/* Label */}
                  <div className="grid gap-2">
                    <Label htmlFor="risk_level">
                      <CustomLabelText
                        required={true}
                        labelText="Risk Level"
                        errorField={errors?.risk_level}
                        bottomAlign={true}
                      />
                    </Label>
                    <Input
                      id="risk_level"
                      type="text"
                      name="risk_level"
                      value={values?.risk_level || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      className={
                        errors?.risk_level &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  {/* Risk Action */}
                  <div className="grid gap-2">
                    <Label htmlFor="risk_action">
                      <CustomLabelText
                        required={true}
                        labelText="Risk Action"
                        errorField={errors?.risk_action}
                        bottomAlign={true}
                      />
                    </Label>
                    <Input
                      id="risk_action"
                      type="text"
                      name="risk_action"
                      value={values?.risk_action || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      autoComplete="off"
                      className={
                        errors?.risk_action &&
                        `focus-visible:ring-red-500 border-red-500`
                      }
                    />
                  </div>

                  {/* Color Picker */}
                  <div className="grid grid-cols-2">
                    <div className="grid gap-2">
                      <Label htmlFor="color">
                        <CustomLabelText
                          required={true}
                          labelText="Color"
                          errorField={errors?.color}
                          bottomAlign={true}
                        />
                      </Label>
                      <ColorPicker
                        value={values?.color}
                        onChange={(e) => setFieldValue("color", e)}
                      />
                    </div>

                    {/* Default Checkbox */}

                    <div className="flex items-center space-x-2 mt-4">
                      <Checkbox
                        id="terms"
                        checked={values.default}
                        name="same_address"
                        onCheckedChange={(checked) =>
                          setFieldValue("default", checked)
                        }
                      />
                      <label
                        htmlFor="terms"
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                      >
                        Default
                      </label>
                    </div>
                  </div>
                </div>

                {/* Dynamic Conditions */}
                <div className="mt-4">
                  <FieldArray name="conditions">
                    {({ push, remove }) => (
                      <div className="space-y-4">
                        {values.conditions.map((cond, index) => (
                          <Card key={index} className="p-4">
                            <div className="flex justify-between items-center mb-4">
                              <h4 className="font-medium">
                                Condition {index + 1}
                              </h4>
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  size="icon"
                                  className="h-6 w-6"
                                  onClick={() =>
                                    push({
                                      sections: [],
                                      operator: "",
                                      total: "",
                                      min: "",
                                      max: "",
                                    })
                                  }
                                >
                                  <Plus className="w-4 h-4" />
                                </Button>
                                {values.conditions.length > 1 && (
                                  <Button
                                    type="button"
                                    variant="destructive"
                                    className="h-6 w-6"
                                    size="icon"
                                    onClick={() => remove(index)}
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </Button>
                                )}
                              </div>
                            </div>

                            {/* Operator */}
                            <div className="grid grid-cols-3 gap-2">
                              <div className="grid gap-2">
                                <Label htmlFor="sections">
                                  <CustomLabelText
                                    required={true}
                                    labelText="Section"
                                    errorField={
                                      errors?.conditions?.[index]?.sections
                                    }
                                    bottomAlign={true}
                                  />
                                </Label>
                                <div className="relative">
                                  <MultiSelect
                                    options={
                                      sectionListing?.length > 0
                                        ? sectionListing
                                        : []
                                    }
                                    placeholder={"Section"}
                                    onValueChange={(e) => {
                                      setFieldValue(
                                        `conditions.${index}.sections`,
                                        e
                                      );
                                    }}
                                    handleBlur={handleBlur}
                                    className={
                                      errors?.conditions?.[index]?.sections &&
                                      `focus-visible:ring-red-500 border-red-500`
                                    }
                                    variant="inverted"
                                    animation={2}
                                    maxCount={0}
                                    defaultValue={
                                      values?.conditions?.[index]?.sections
                                    }
                                  />
                                  {sectionLoading && (
                                    <span className="absolute top-2 right-2 animate-spin">
                                      <Loader2 width={20} height={20} />
                                    </span>
                                  )}
                                </div>
                              </div>
                              <div className="grid gap-2">
                                <Label htmlFor={`conditions.${index}.operator`}>
                                  <CustomLabelText
                                    required={true}
                                    labelText="Operator"
                                    errorField={
                                      errors?.conditions?.[index]?.operator
                                    }
                                  />
                                </Label>
                                <div className="relative">
                                  <SearchSelect
                                    frameworks={operatorOptions}
                                    optionType="Operator"
                                    handleSelect={(val) =>
                                      setFieldValue(
                                        `conditions.${index}.operator`,
                                        val
                                      )
                                    }
                                    initialValue={cond.operator}
                                    className={
                                      errors?.conditions?.[index]?.operator &&
                                      `focus-visible:ring-red-500 border-red-500`
                                    }
                                  />
                                </div>
                              </div>

                              {/* Values */}
                              {cond.operator === "Between" ? (
                                <div className="grid grid-cols-2 gap-4">
                                  <div className="grid gap-2">
                                    <Label htmlFor={`conditions.${index}.min`}>
                                      <CustomLabelText
                                        required={true}
                                        labelText="Min"
                                        errorField={
                                          errors?.conditions?.[index]?.min
                                        }
                                      />
                                    </Label>
                                    <div className="relative">
                                      <Input
                                        type="number"
                                        placeholder="Min"
                                        value={cond.min}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `conditions.${index}.min`,
                                            e.target.value
                                          )
                                        }
                                        className={
                                          errors?.conditions?.[index]?.min &&
                                          `focus-visible:ring-red-500 border-red-500`
                                        }
                                      />
                                    </div>
                                  </div>
                                  <div className="grid gap-2">
                                    <Label htmlFor={`conditions.${index}.max`}>
                                      <CustomLabelText
                                        required={true}
                                        labelText="Max"
                                        errorField={
                                          errors?.conditions?.[index]?.max
                                        }
                                      />
                                    </Label>
                                    <div className="relative">
                                      <Input
                                        type="number"
                                        placeholder="Max"
                                        value={cond.max}
                                        onChange={(e) =>
                                          setFieldValue(
                                            `conditions.${index}.max`,
                                            e.target.value
                                          )
                                        }
                                        className={
                                          errors?.conditions?.[index]?.max &&
                                          `focus-visible:ring-red-500 border-red-500`
                                        }
                                      />
                                    </div>
                                  </div>
                                </div>
                              ) : (
                                <div className="grid gap-2">
                                  <Label htmlFor={`conditions.${index}.total`}>
                                    <CustomLabelText
                                      required={true}
                                      labelText="Total"
                                      errorField={
                                        errors?.conditions?.[index]?.total
                                      }
                                    />
                                  </Label>
                                  <div className="relative">
                                    <Input
                                      type="number"
                                      placeholder="Total"
                                      value={cond.total}
                                      onChange={(e) =>
                                        setFieldValue(
                                          `conditions.${index}.total`,
                                          e.target.value
                                        )
                                      }
                                      className={
                                        errors?.conditions?.[index]?.total &&
                                        `focus-visible:ring-red-500 border-red-500`
                                      }
                                    />
                                  </div>
                                </div>
                              )}
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </FieldArray>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="modal-footer pt-4 border-t mt-4 flex items-center justify-end">
              <Button type="submit" disabled={!isValid}>
                Save
              </Button>
              <Button
                className="reset ml-3"
                data-dismiss="modal"
                aria-label="Close"
                onClick={(e) => {
                  e.preventDefault();
                  toggleShowAddEditCondition();
                }}
                variant="secondary"
              >
                Cancel
              </Button>
            </div>
          </form>
        </>
      )}
    </Formik>
  );
};

export default AddEditCondition;
